import { useEffect, useMemo, useRef, useState } from 'react'
import { Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import {
  useLocation,
  useNavigationType,
  useSearchParams,
} from 'react-router-dom'
import { PublicPageShell } from '@/features/marketing/components/PublicPageShell'
import { Pagination } from '@/shared/components/ui/pagination'
import {
  FeaturedReleaseNote,
  type EstadoNotaNoEncontrada,
} from './FeaturedReleaseNote'
import { ReleaseFilterBubbles } from './ReleaseFilterBubbles'
import { ReleaseNoteCard } from './ReleaseNoteCard'
import { usePortalSession } from './usePortalSession'
import { agruparBurbujasPorProducto, grupoSeleccionado } from './productGroups'
import {
  fetchMyRatings,
  fetchProducts,
  fetchReleases,
  type ReleaseProductOption,
  type ReleasesResult,
} from './releases'

const PAGE_SIZE = 5

/**
 * El universo de burbujas tiene TRES estados, no dos: mientras está
 * `pendiente` no se sabe a qué aplicación pertenece el `?repo=` de la URL, y
 * `falla` significa que no hay pliegue posible (ni burbujas).
 */
type UniversoBurbujas =
  | { estado: 'pendiente' }
  | { estado: 'listo'; opciones: ReleaseProductOption[] }
  | { estado: 'falla' }

/**
 * Página pública `/actualizaciones`. Con `?repo=<repo>&version=<versión>` abre
 * UNA nota (`FeaturedReleaseNote`): es el enlace del aviso de WhatsApp que
 * manda el portal-server al publicar una nota (Víctor, 2026-10-21). Sin las
 * dos, el histórico de siempre.
 *
 * Pasar de la nota al histórico es la misma ruta (`/actualizaciones`), así que
 * `RootLayout` no reinicia el desplazamiento: se reinicia aquí, con el mismo
 * criterio — `instant` explícito y nunca en una navegación POP.
 */
export function ReleasesHistoryPage() {
  const [params] = useSearchParams()
  const navigationType = useNavigationType()
  const repo = params.get('repo')?.trim() ?? ''
  const version = params.get('version')?.trim() ?? ''
  const unaNota = Boolean(repo && version)

  const vistaAnterior = useRef(unaNota)
  useEffect(() => {
    if (vistaAnterior.current === unaNota) return
    vistaAnterior.current = unaNota
    if (navigationType === 'POP') return
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' })
  }, [unaNota, navigationType])

  if (unaNota) return <FeaturedReleaseNote repo={repo} version={version} />
  return <ReleasesHistory />
}

/**
 * El histórico (goal portal-notas-de-version-en-vivo): todas las notas de
 * versión, más reciente primero, con paginación SERVER-SIDE desde el día uno
 * (a diferencia de NewsListPage, que carga todo y filtra en memoria). Cada nota
 * va completa (Markdown) con sus estrellas; el voto propio se hidrata si hay
 * sesión.
 */
function ReleasesHistory() {
  const { t } = useTranslation()
  const { ensureSession } = usePortalSession()
  const [params, setParams] = useSearchParams()
  // La nota del enlace no estaba (`FeaturedReleaseNote` cae aquí con `replace`):
  // una línea que lo dice. Va en el `state` de la navegación, así que se va
  // sola al cambiar de burbuja, que navega sin él.
  const notaNoEncontrada = Boolean(
    (useLocation().state as EstadoNotaNoEncontrada | null)?.notaNoEncontrada,
  )
  const [loading, setLoading] = useState(true)
  const [data, setData] = useState<ReleasesResult | null>(null)
  const [myRatings, setMyRatings] = useState<Record<string, number>>({})
  const [universo, setUniverso] = useState<UniversoBurbujas>({
    estado: 'pendiente',
  })

  // Una aplicación = una burbuja, aunque se corte desde dos repos (goal 7). El
  // pliegue es una función pura: la página solo la cablea.
  const grupos = useMemo(
    () =>
      universo.estado === 'listo'
        ? agruparBurbujasPorProducto(universo.opciones)
        : [],
    [universo],
  )

  // La selección vive en la URL: el enlace se puede compartir y el «atrás» del
  // navegador la restituye (mismo criterio que TicketsPage).
  const repoUrl = params.get('repo')
  // Con un filtro en la URL no se puede pedir nada hasta saber a qué grupo
  // pertenece: pedir el repo suelto y luego el CSV serían DOS peticiones, y
  // entremedias la pantalla contradiría a su propio botón.
  const esperandoUniverso = repoUrl !== null && universo.estado === 'pendiente'
  // Lo que viaja al servidor es el CSV del grupo. Sin universo (la petición de
  // burbujas falló) no hay pliegue posible y el filtro de la URL vale tal cual:
  // el histórico sigue funcionando igual, solo que sin burbujas (raíz §6.6).
  const repo =
    universo.estado === 'falla' ? repoUrl : grupoSeleccionado(grupos, repoUrl)

  // Un enlace viejo con UN repo del grupo se normaliza al CSV completo, con
  // `replace`: la burbuja marcada y la lista mostrada tienen que decir lo
  // mismo. Al quedar ya normalizada, la condición no se vuelve a cumplir.
  useEffect(() => {
    if (repo === null || repo === repoUrl) return
    const nextParams = new URLSearchParams(params)
    nextParams.set('repo', repo)
    setParams(nextParams, { replace: true })
  }, [repo, repoUrl, params, setParams])

  // La página vive JUNTO al repo al que pertenece y se deriva en render: al
  // cambiar de burbuja —también con el «atrás»— se vuelve a la 1, porque la
  // página 3 de «todas» no tiene por qué existir para una sola aplicación.
  const [paged, setPaged] = useState<{ repo: string | null; page: number }>({
    repo: null,
    page: 1,
  })
  const page = paged.repo === repo ? paged.page : 1

  useEffect(() => {
    if (esperandoUniverso) return
    let active = true
    ;(async () => {
      let result: ReleasesResult
      try {
        result = await fetchReleases({ page, pageSize: PAGE_SIZE, repo })
      } catch {
        if (active) setLoading(false)
        return
      }
      if (!active) return
      // El contenido está listo: se muestra YA, sin esperar a la sesión. La
      // hidratación del voto propio es secundaria (best-effort) y NO debe
      // bloquear el render tras un refresh silencioso que además arrastra el
      // import dinámico del cliente Apollo.
      setData(result)
      setLoading(false)
      if (!result.items.length) return
      try {
        const token = await ensureSession()
        if (!token || !active) return
        const mine = await fetchMyRatings(result.items.map((note) => note.id))
        if (!active) return
        setMyRatings(
          Object.fromEntries(mine.map((m) => [m.releaseNoteId, m.score])),
        )
      } catch {
        // Sin sesión o sin ratings: se queda con el promedio público.
      }
    })()
    return () => {
      active = false
    }
  }, [page, repo, esperandoUniverso, ensureSession])

  // Las burbujas son una AYUDA de navegación, no la página: si esta petición
  // falla no se pintan y el histórico sigue funcionando igual (raíz §6.6).
  useEffect(() => {
    let active = true
    fetchProducts()
      .then((items) => {
        if (active) setUniverso({ estado: 'listo', opciones: items })
      })
      .catch(() => {
        if (active) setUniverso({ estado: 'falla' })
      })
    return () => {
      active = false
    }
  }, [])

  const changePage = (next: number) => {
    setPaged({ repo, page: next })
    setLoading(true)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const changeRepo = (next: string | null) => {
    const nextParams = new URLSearchParams(params)
    if (next) nextParams.set('repo', next)
    else nextParams.delete('repo')
    // `replace` para no apilar una entrada del historial por burbuja pulsada.
    setParams(nextParams, { replace: true })
    setPaged({ repo: next, page: 1 })
    setLoading(true)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  return (
    <PublicPageShell>
      <div className="mb-10">
        <span className="text-xs font-bold uppercase tracking-widest text-primary">
          {t('releases.badge')}
        </span>
        <h1 className="mt-2 text-3xl font-bold tracking-tight lg:text-4xl">
          {t('releases.historyTitle')}
        </h1>
        <p className="mt-3 max-w-2xl text-muted-foreground">
          {t('releases.historyDescription')}
        </p>
        <ReleaseFilterBubbles
          className="mt-6"
          options={grupos}
          selected={repo}
          onSelect={changeRepo}
        />
        {notaNoEncontrada && (
          <p role="status" className="mt-6 text-sm text-muted-foreground">
            {t('releases.noteNotFound')}
          </p>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-muted-foreground">
          <Loader2 className="mr-2 size-5 animate-spin" />
          {t('common.loading')}
        </div>
      ) : !data || data.items.length === 0 ? (
        <p className="text-muted-foreground">
          {/* Vacío honesto: filtrando, el histórico SÍ tiene notas — las que no
              hay son las de esa aplicación. */}
          {repo ? t('releases.historyEmptyFiltered') : t('releases.historyEmpty')}
        </p>
      ) : (
        <>
          <div className="space-y-8">
            {data.items.map((note) => (
              <ReleaseNoteCard
                key={note.id}
                note={note}
                myScore={myRatings[note.id] ?? null}
              />
            ))}
          </div>

          <Pagination
            className="mt-10"
            page={data.page}
            pageSize={data.pageSize}
            totalItems={data.total}
            onPageChange={changePage}
          />
        </>
      )}
    </PublicPageShell>
  )
}
